<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import type { SharePayload, ShareSite, ShareTrackEvent } from './types'
import { payloadFromElement } from './dom'
import { safeShareUrl } from './channels'
import ShareModal from './ShareModal.vue'
import ChallengeBanner from './ChallengeBanner.vue'

const props = defineProps<{
  site: ShareSite
  sitePayload?: SharePayload
  resolveChallenge?: (location: Location) => string | null
  onTrack?: (event: ShareTrackEvent) => void
}>()

const current = ref<SharePayload | null>(null)
const banner = ref('')

const dismissKey = () => `share-kit:banner:${window.location.pathname}`

function open(payload: SharePayload) {
  if (!safeShareUrl(payload.url)) return
  current.value = payload
}

function close() {
  current.value = null
}

function dismissBanner() {
  banner.value = ''
  try {
    sessionStorage.setItem(dismissKey(), '1')
  } catch {
    // 隐私模式下记不住关闭状态，本次访问不再重复插入即可
  }
}

function onClick(event: MouseEvent) {
  const target = event.target
  if (!(target instanceof Element)) return
  if (target.closest('[data-share-site]')) {
    if (!props.sitePayload) return
    event.preventDefault()
    open(props.sitePayload)
    return
  }
  const button = target.closest('[data-share-open]')
  if (!button) return
  const root = button.closest('[data-share-root]')
  if (!(root instanceof HTMLElement)) return
  const payload = payloadFromElement(root)
  if (!payload) return
  event.preventDefault()
  open(payload)
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') close()
}

onMounted(() => {
  const message = props.resolveChallenge?.(window.location) || ''
  if (message) {
    let dismissed = false
    try {
      dismissed = sessionStorage.getItem(dismissKey()) === '1'
    } catch {
      dismissed = false
    }
    if (!dismissed) banner.value = message
  }
  document.addEventListener('click', onClick)
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', onClick)
  document.removeEventListener('keydown', onKeydown)
})

defineExpose({ open })
</script>

<template>
  <div>
    <Teleport to="main">
      <ChallengeBanner v-if="banner" :message="banner" @close="dismissBanner" />
    </Teleport>
    <ShareModal
      v-if="current"
      :site="site"
      :payload="current"
      @close="close"
      @track="props.onTrack?.($event)"
    />
  </div>
</template>
